import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';

import { ChooserContainer as Chooser } from './Chooser';
import { DashboardContainer as Dashboard } from './DashboardReport';
import { DashboardGoalsContainer as DashboardGoals } from './DashboardGoals';
import { StartOrSkipAssessmentContainer as StartOrSkipAssessment } from './StartOrSkipAssessment';

import { MouseCatcherContainer as MouseCatcher } from '../../app/util/MouseCatcher';
import { launchpadSelectors, launchpadActions } from '../reducer/launchpad-reducer';
import { questionnaireActions } from '../../questionnaire/reducer/questionnaire-reducer';
import { preferencesActions } from '../../preferences/reducer/preferences-reducer';
import { appMainBackground, dashboardGoalsBackground, white, darkRed, appTextColor } from '../../../styles/colors';
import { TestAlertContainer as TestAlert } from './TestAlert';
import { TestWarningContainer as TestWarning } from './TestWarning';
import { EmailLauncherContainer as EmailLauncher } from './EmailLauncher';
import { appMinWidth } from '../../../styles/constants';
import { lionSelectors } from '@rosetta/react-lion/dist/index';
import { authSelectors, LOGIN_STATUS } from '@rosetta/react-auth/dist/index';
import { appSelectors } from '../../app/reducer/app-reducer';
import { withWindowDimensions } from '@rosetta/react-common-ui';
import { isMobile as isMobileDetected } from 'react-device-detect';
import EndInteraction from '../../app/components/EndInteraction';
import { endInteraction } from '@rosetta/eve-client';

const chooserWidth = 1076;

const LaunchpadDiv = styled.div((props) => {
  let style = {
    position: !props.isMobile ? 'absolute' : 'relative',
    top: '0px',
    bottom: '0px',
    overflow: 'auto',
    width: '100%',
    minWidth: !props.isMobile ? appMinWidth : '320px',
    background: appMainBackground,
    paddingBottom: !props.isMobile ? '0px' : '20px'
  };

  if (!props.isCatalyst) {
    style.display = 'flex';
    style.alignItems = 'center';
    style.justifyContent = 'center';
  }

  if (props.isDashboard) {
    style.background = 'linear-gradient(' + dashboardGoalsBackground + ' 256px, ' + appMainBackground + ' 256px)';
  } else {
    style.textAlign = 'center';
  }

  if (props.isMobile) {
    style.minHeight = props.windowHeight - 215 + 'px';
  }
  return style;
});

const LaunchpadContentWrapperDiv = styled.div((props) => {
  let style = {
    width: !props.isMobile ? '100%' : 'auto',
    display: !props.isMobile ? 'flex' : 'block',
    alignItems: 'top',
    justifyContent: 'center',
    margin: !props.isMobile ? '0' : '0 16px'
  };
  return style;
});

const SalutationContainer = styled.div((props) => {
  let style = {
    display: !props.isMobile ? 'block' : 'none',
    fontFamily: 'inherit',
    paddingTop: !props.isMobile ? '60px' : '70px',
    position: 'relative',
    color: props.isDashboard ? white : appTextColor,
    width: !props.isMobile ? chooserWidth + 'px' : 'auto',
    fontSize: '14px',
    margin: 'auto'
  };

  return style;
});

const ImpersonationNotificaton = styled.div(() => {
  let style = {
    color: darkRed,
    fontSize: '16px',
    lineHeight: '28px',
    float: 'left',
    fontWeight: 400,
    width: '55%',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    paddingRight: '10px'
  };

  return style;
});

const UserName = styled.div((props) => {
  let style = {
    fontSize: '24px',
    lineHeight: '30px',
    marginBottom: '20px',
    fontWeight: '200',
    marginLeft: '25px',
    ' b': {
      fontWeight: '500'
    }
  };

  return style;
});

const ChooserWrapperDiv = styled.div(() => {
  let style = {};
  return style;
});

const EmailLauncherMobile = styled.div((props) => {
  let style = {
    display: !props.isMobile ? 'none' : 'block'
  };
  return style;
});

const EmailLauncherWeb = styled.div((props) => {
  let style = {};

  style.padding = '0 0 15px 15px';
  style.display = 'table-cell';
  style.verticalAlign = 'top';

  if (props.isMobile) {
    style.display = 'none';
  }
  return style;
});

//*********************************************
export class Launchpad extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      // this is here to allow auto-launch code to only fire once
      redirecting: false,
      showLaunchpad: false
    };
    props.initRecommendations();
    props.getLearnerConfigs();
    props.getLearnerCanChangeL2();
  }

  static getDerivedStateFromProps = (nextProps, prevState) => {
    if (nextProps.isAppInitialized && nextProps.loginStatus !== LOGIN_STATUS.LOGGED_IN) {
      nextProps.history.push('/login');
    }
    if (nextProps.recommendationsLoaded) {
      // Handle auto-launch of test or questionnaire if needed
      if (
        !prevState.redirecting &&
        nextProps.recommendations &&
        nextProps.recommendations.length === 1 &&
        nextProps.recommendations[0].category &&
        (nextProps.recommendations[0].category === 'questionnaire' || nextProps.recommendations[0].category === 'LTS')
      ) {
        // will redirect somewhere so don't set showLaunchpad to true
        endInteraction('application-startup');
        nextProps.launchRecommendation(nextProps.recommendations[0]);
        return { redirecting: true };
      }
      return { showLaunchpad: true };
    }
    return null;
  };

  render = () => {
    if (!this.props.lionLoaded) {
      return <MouseCatcher />;
    }
    if (!this.state.showLaunchpad) {
      return <MouseCatcher />;
    }
    const isMobile = isMobileDetected;

    const isDashboard =
      (this.props.dashboard && this.props.dashboard.showDashboard) ||
      (this.props.userInfo && this.props.userInfo.usageGoalDays);

    let testAlert = null;
    if (this.props.optionalTrigger) {
      testAlert = <TestAlert />;
    }
    let testWarning = null;
    if (this.props.optionalWarning && !this.props.optionalTrigger) {
      testWarning = <TestWarning />;
    }

    let emailLauncher = null;
    if (this.props.mobileEmailLauncher) {
      emailLauncher = <EmailLauncher />;
    }

    let DashboardContentContainer = null;
    let impersonationNotificationBlock = null;

    if (this.props.clientId === 'client.impersonate') {
      impersonationNotificationBlock = (
        <ImpersonationNotificaton>
          {this.props.getLionTranslatedString('_dashboard_impersonation_notification', '', '', 'en-US')}
        </ImpersonationNotificaton>
      );
    }

    let haveUser = this.props.userName;

    let hasCatalyst = false;
    if (this.props.recommendations) {
      this.props.recommendations.forEach((productRight) => {
        hasCatalyst = true;
      });
    }

    if (haveUser && hasCatalyst) {
      DashboardContentContainer = (
        <SalutationContainer isDashboard={isDashboard} isMobile={isMobile}>
          {impersonationNotificationBlock}
          <UserName
            isMobile={isMobile}
            data-qa="DashboardUserName"
            dangerouslySetInnerHTML={{
              __html: this.props
                .getLionTranslatedString(
                  '_dashboard_salutation_' +
                    (this.props.userInfo.targetLanguage.language
                      ? this.props.userInfo.targetLanguage.language
                      : 'en-US')
                )
                .replace('{name}', haveUser)
            }}
          />
        </SalutationContainer>
      );
    }

    let launchpadDivContent = (
      <div>
        {/* Send application startup for users who bypassed login screen (sso) */}
        <EndInteraction interaction="application-startup" />
        {DashboardContentContainer}

        <DashboardGoals isCatalyst={hasCatalyst} isDashboard={isDashboard} />
        <LaunchpadContentWrapperDiv isMobile={isMobile}>
          <ChooserWrapperDiv>
            <Chooser />
            <EmailLauncherWeb isMobile={isMobile}>{emailLauncher}</EmailLauncherWeb>
          </ChooserWrapperDiv>
        </LaunchpadContentWrapperDiv>
        <Dashboard isCatalyst={hasCatalyst} />
        <EmailLauncherMobile isMobile={isMobile}>{emailLauncher}</EmailLauncherMobile>
      </div>
    );

    let LaunchpadDivContainer = null;
    if (this.props.skipPlacementPopup.show) {
      LaunchpadDivContainer = (
        <LaunchpadDiv
          isDashboard={isDashboard}
          isCatalyst="true"
          isMobile={isMobile}
          windowHeight={this.props.clientHeight}
        >
          <StartOrSkipAssessment recommendation={this.props.skipPlacementPopup.recommendation} />
        </LaunchpadDiv>
      );
    } else if (hasCatalyst) {
      LaunchpadDivContainer = (
        <LaunchpadDiv
          isDashboard={isDashboard}
          isCatalyst="true"
          isMobile={isMobile}
          windowHeight={this.props.clientHeight}
        >
          {launchpadDivContent}
        </LaunchpadDiv>
      );
    } else {
      LaunchpadDivContainer = (
        <LaunchpadDiv isDashboard={isDashboard} isMobile={isMobile} windowHeight={this.props.clientHeight}>
          {launchpadDivContent}
        </LaunchpadDiv>
      );
    }
    return (
      <div>
        {testAlert}
        {testWarning}
        {LaunchpadDivContainer}
      </div>
    );
  };
}

Launchpad.propTypes = {};

const mapStateToProps = (state, ownprops) => ({
  lionLoaded: lionSelectors.getLionAppResourcesLoaded(state),
  getLionTranslatedString: (key, placeholders, values, altLocale) =>
    lionSelectors.getLionTranslatedString(state, key, placeholders, values, altLocale),
  isAppInitialized: appSelectors.isAppInitialized(state),
  mobileEmailLauncher: launchpadSelectors.mobileEmailLauncher(state),
  optionalWarning: launchpadSelectors.optionalWarning(state),
  optionalTrigger: launchpadSelectors.optionalTrigger(state),
  recommendations: launchpadSelectors.recommendations(state),
  recommendationsLoaded: launchpadSelectors.recommendationsLoaded(state),
  skipPlacementPopup: launchpadSelectors.skipPlacementPopup(state),
  clientId: authSelectors.clientId(state),
  userName: authSelectors.userName(state),
  userInfo: authSelectors.userInfo(state),
  loginStatus: authSelectors.loginStatus(state),
  dashboard: launchpadSelectors.dashboard(state)
});

const wrapActions = {
  initRecommendations: launchpadActions.initRecommendations,
  launchRecommendation: launchpadActions.launchRecommendation,
  getLearnerCanChangeL2: preferencesActions.getLearnerCanChangeL2,
  getLearnerConfigs: questionnaireActions.getLearnerConfigs
};
export const LaunchpadContainer = connect(
  mapStateToProps,
  wrapActions
)(withWindowDimensions(Launchpad));



// WEBPACK FOOTER //
// ./src/modules/launchpad/components/Launchpad.jsx